import PageHeader from '../../components/PageHeader';
import React, { useEffect, useState } from 'react';
import { Twitter, Plug, KeyRound, Trash2 } from 'lucide-react';
import api from '../../services/api';
import { cn } from '../../utils/cn';
import alertService from '../../utils/alert';

const unwrap = (res) => res?.data?.data ?? {};
const errMsg = (e) => e?.response?.data?.error || e?.response?.data?.message || e.message || 'Something went wrong';

const FIELD_BASE = 'w-full px-4 bg-[#F3F2F2] border border-[#E5E5E5] rounded text-[13px] outline-none focus:bg-white focus:border-[#0176D3] transition-all';
const inputClass = `${FIELD_BASE} h-10`;
const btnPrimaryClass = 'h-10 px-4 bg-[#0176D3] text-white rounded text-[13px] font-bold hover:bg-[#014486] transition-all shadow-[0_2px_8px_rgba(0,0,0,0.16)] disabled:opacity-50 disabled:cursor-not-allowed';

function Banner({ msg }) {
  if (!msg) return null;
  return (
    <div className={cn(
      'rounded px-4 py-3 text-[13px] font-medium border',
      msg.type === 'success' ? 'bg-[#EBF3ED] text-[#2E844A] border-[#C4DCC8]' : 'bg-[#FDE7E9] text-[#BA0517] border-[#F6A3AB]'
    )}>
      {msg.text}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="block text-[11px] font-semibold text-[#706E6B]">{label}</span>
      {children}
    </label>
  );
}

// Minimal connect-only settings page — X posts are composed via Cross-Post,
// not a dedicated native composer here, so this page only needs Credentials
// + Account tabs (mirrors LinkedInPage's structure, trimmed down).
export default function XSettingsPage() {
  const [tab, setTab] = useState('account');
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('x_connected')) {
      setBanner({ type: 'success', text: 'X account connected.' });
      setTab('account');
    } else if (params.get('x_error')) {
      setBanner({ type: 'error', text: `X connection failed: ${params.get('x_error')}` });
    }
    if (params.get('x_connected') || params.get('x_error')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <PageHeader title="X (Twitter)" subtitle="Connect your own X app to use X as a Cross-Post target." icon={Twitter} />

      <Banner msg={banner} />

      <div className="flex items-center border-b border-[#E5E5E5] gap-6 overflow-x-auto">
        {[{ key: 'credentials', label: 'App Keys', icon: KeyRound }, { key: 'account', label: 'Account', icon: Plug }].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cn(
              'flex items-center gap-2 pb-3 text-[13px] font-bold border-b-2 transition-all whitespace-nowrap shrink-0',
              tab === t.key ? 'border-[#0176D3] text-[#0176D3]' : 'border-transparent text-[#5C5C5C] hover:text-[#2E2E2E]'
            )}
          >
            <t.icon size={15} />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'credentials' && <CredentialsTab />}
      {tab === 'account' && <AccountTab />}
    </div>
  );
}

function CredentialsTab() {
  const [urls, setUrls] = useState(null);
  const [form, setForm] = useState({ clientId: '', clientSecret: '', redirectUri: '', scopes: '' });
  const [hasSecret, setHasSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      const [u, c] = await Promise.all([api.get('/x/urls'), api.get('/x/credentials')]);
      const urlsData = unwrap(u);
      setUrls(urlsData);
      const cred = unwrap(c).credential;
      if (cred) {
        setForm({ clientId: cred.clientId || '', clientSecret: '', redirectUri: cred.redirectUri || urlsData.callbackUrl || '', scopes: cred.scopes || urlsData.suggestedScopes || '' });
        setHasSecret(!!cred.hasClientSecret);
      } else {
        setForm((f) => ({ ...f, redirectUri: urlsData.callbackUrl || '', scopes: urlsData.suggestedScopes || '' }));
      }
    } catch (e) {
      setMsg({ type: 'error', text: errMsg(e) });
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      await api.put('/x/credentials', form);
      setMsg({ type: 'success', text: 'App keys saved. You can now connect your X account.' });
      setHasSecret(true);
    } catch (e2) {
      setMsg({ type: 'error', text: errMsg(e2) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="sf-card p-5 bg-[#EAF4FE] border-[#B8DAF3] text-[13px]">
        <p className="font-bold text-[#014486] mb-2">Set up your own X (Twitter) app</p>
        <ol className="list-decimal ml-5 space-y-1 text-[#0176D3]">
          <li>Create a project/app at <a className="underline font-semibold" href="https://developer.x.com/en/portal/dashboard" target="_blank" rel="noreferrer">developer.x.com</a> with OAuth 2.0 enabled (confidential client).</li>
          <li>Under User authentication settings, add this exact <b>Callback URI / Redirect URL</b>:</li>
        </ol>
        {urls?.callbackUrl && (
          <code className="block mt-2 bg-white border border-[#E5E5E5] rounded px-3 py-2 text-[12px] break-all text-[#2E2E2E]">{urls.callbackUrl}</code>
        )}
        <p className="mt-2 text-[#0176D3]">Then paste your <b>Client ID</b> and <b>Client Secret</b> below. These are stored only for your account.</p>
      </div>

      <Banner msg={msg} />

      <form onSubmit={save} className="sf-card p-6 space-y-4 max-w-2xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Client ID">
            <input className={inputClass} value={form.clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value })} required />
          </Field>
          <Field label={`Client Secret${hasSecret ? ' (saved — leave blank to keep)' : ''}`}>
            <input className={inputClass} type="password" value={form.clientSecret} placeholder={hasSecret ? '••••••••' : ''} onChange={(e) => setForm({ ...form, clientSecret: e.target.value })} required={!hasSecret} />
          </Field>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Redirect URI">
            <input className={inputClass} value={form.redirectUri} onChange={(e) => setForm({ ...form, redirectUri: e.target.value })} />
          </Field>
          <Field label="Scopes (space-separated)">
            <input className={inputClass} value={form.scopes} onChange={(e) => setForm({ ...form, scopes: e.target.value })} />
          </Field>
        </div>
        <button disabled={saving} className={btnPrimaryClass}>{saving ? 'Saving…' : 'Save credentials'}</button>
      </form>
    </div>
  );
}

function AccountTab() {
  const [accounts, setAccounts] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      const all = unwrap(await api.get('/x/accounts')).accounts || [];
      setAccounts(all.filter((a) => a.status !== 'revoked'));
    } catch (e) {
      setMsg({ type: 'error', text: errMsg(e) });
    }
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    setConnecting(true);
    setMsg(null);
    try {
      const { url } = unwrap(await api.get('/x/connect'));
      window.location.href = url;
    } catch (e) {
      setConnecting(false);
      setMsg({ type: 'error', text: errMsg(e) });
    }
  };

  const disconnect = async (id) => {
    if (!(await alertService.confirm('Disconnect?', 'Disconnect this X account? It will no longer appear as a Cross-Post target.', 'danger', 'Disconnect'))) return;
    try {
      await api.delete(`/x/accounts/${id}`);
      load();
    } catch (e) {
      setMsg({ type: 'error', text: errMsg(e) });
    }
  };

  return (
    <div className="space-y-5">
      <Banner msg={msg} />
      <button onClick={connect} disabled={connecting} className={cn(btnPrimaryClass, 'flex items-center gap-2')}>
        <Plug size={15} /> {connecting ? 'Redirecting…' : 'Connect X'}
      </button>

      {accounts.length === 0 ? (
        <div className="sf-card text-center py-16 px-4 space-y-3">
          <div className="w-14 h-14 bg-[#EAF4FE] rounded-full flex items-center justify-center mx-auto">
            <Twitter size={26} className="text-[#0176D3]" />
          </div>
          <p className="font-bold text-[#2E2E2E] text-[13px]">No X accounts connected</p>
          <p className="text-[12px] text-[#5C5C5C] max-w-sm mx-auto">Save your credentials first, then connect your account.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {accounts.map((acc) => (
            <div key={acc._id} className="sf-card p-4 flex items-center gap-3 hover:border-[#0176D3] transition-all">
              {acc.profileImageUrl ? <img src={acc.profileImageUrl} alt="" className="w-10 h-10 rounded-full shrink-0" /> : <div className="w-10 h-10 rounded-full bg-[#F3F2F2] shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[#2E2E2E] text-[13px] truncate">{acc.name}</div>
                <div className="text-[12px] text-[#5C5C5C] truncate">@{acc.username}</div>
              </div>
              <button onClick={() => disconnect(acc._id)} className="h-7 w-7 flex items-center justify-center rounded text-[#5C5C5C] hover:text-[#BA0517] hover:bg-[#FDE7E9] transition-colors" title="Disconnect">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
